import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { authApi, usersApi } from "../api";
import type { User } from "../types";
import {
  clearAuthToken,
  clearStoredUser,
  readAuthToken,
  readRefreshToken,
  readStoredUserRaw,
  writeAuthToken,
  writeRefreshToken,
  writeStoredUserRaw,
} from "../utils/authStorage";
import { clearWorkspace } from "../utils/workspaceStorage";

export type AuthSession = {
  token: string;
  refreshToken?: string;
  user: User;
};

type AuthState = {
  token: string | null;
  user: User | null;
  setSession: (session: AuthSession | null) => void;
  applyUser: (next: User) => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

function readStoredUser(): User | null {
  try {
    const raw = readStoredUserRaw();
    if (!raw) return null;
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(readAuthToken);
  const [user, setUserState] = useState<User | null>(() => {
    if (!readAuthToken()) return null;
    return readStoredUser();
  });

  const applyUser = useCallback((next: User) => {
    writeStoredUserRaw(JSON.stringify(next));
    setUserState(next);
  }, []);

  const setSession = useCallback((session: AuthSession | null) => {
    if (!session) {
      // Best-effort server-side revoke; never block clearing local state on this.
      const refreshToken = readRefreshToken();
      if (refreshToken) {
        void authApi.logout(refreshToken).catch(() => {
          /* ignore — local session is cleared regardless */
        });
      }
      clearAuthToken();
      clearStoredUser();
      clearWorkspace();
      setTokenState(null);
      setUserState(null);
      return;
    }
    writeAuthToken(session.token);
    if (session.refreshToken) {
      writeRefreshToken(session.refreshToken);
    }
    writeStoredUserRaw(JSON.stringify(session.user));
    setTokenState(session.token);
    setUserState(session.user);
  }, []);

  const refreshUser = useCallback(async () => {
    const t = token ?? readAuthToken();
    if (!t) return;
    try {
      const next = await usersApi.me(t);
      applyUser(next);
    } catch {
      /* keep cached user until backend exposes GET /users/me */
    }
  }, [token, applyUser]);

  useEffect(() => {
    if (!token) return;
    void refreshUser();
  }, [token, refreshUser]);

  const value = useMemo(
    () => ({
      token,
      user,
      setSession,
      applyUser,
      refreshUser,
    }),
    [token, user, setSession, applyUser, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
